import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  MapPin,
  CalendarDays,
  Music2,
  Sparkles,
  Baby,
  Mic2,
  Send,
  Loader2,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import TurCetate from "@/components/TurCetate";
import { FadeUp, Overline } from "@/components/Reveal";
import { RiseWordmark, StarField } from "@/components/RiseWordmark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  {
    id: "stage-mare",
    icon: Music2,
    name: "Scena Mare",
    place: "Platoul Central al Cetății",
    text: "Capul de afiș, producție de lumini și sunet la scară mare, cu bastioanele pe post de decor.",
  },
  {
    id: "stage-bastion",
    icon: Mic2,
    name: "Scena Bastion",
    place: "Șanțurile Vauban",
    text: "Concerte și DJ set-uri până târziu, freestyle și sunet underground, între ziduri de cărămidă.",
  },
  {
    id: "stage-kidz",
    icon: Baby,
    name: "Scena Kidz",
    place: "Parcul de sub ziduri",
    text: "Spectacole, teatru istoric și jocuri pentru copii și familii, în prima parte a zilei.",
  },
  {
    id: "stage-ateliere",
    icon: Sparkles,
    name: "Zona Ateliere",
    place: "Curțile interioare",
    text: "Graffiti, producție muzicală, dans și povești despre cetate — învățare prin creație.",
  },
];

const ARTISTS = [
  { id: "artist-1", slot: "Headliner · Ziua 1", name: "Anunțat curând", tag: "Concert" },
  { id: "artist-2", slot: "Headliner · Ziua 2", name: "Anunțat curând", tag: "Concert" },
  { id: "artist-3", slot: "Invitat Special", name: "Anunțat curând", tag: "Pop / Urban" },
  { id: "artist-4", slot: "Rising Star", name: "Anunțat curând", tag: "Hip-Hop" },
  { id: "artist-5", slot: "Closing DJ Set", name: "Anunțat curând", tag: "Dance / Electronic" },
  { id: "artist-6", slot: "Showcase Local", name: "Artiști din Alba", tag: "Open Call" },
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-carddark px-4 py-3.5 text-sm text-neutral-100 placeholder:text-neutral-500 transition-colors duration-300 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon";

const Hero = () => (
  <section
    data-testid="rise27-hero"
    className="relative flex min-h-[88svh] items-end overflow-hidden"
  >
    <div className="absolute inset-0">
      <img
        src="/assets/rise27-logo-space.webp"
        alt=""
        aria-hidden="true"
        className="h-full w-full scale-150 object-cover opacity-45 blur-[28px]"
      />
      <div className="absolute inset-0 bg-[#05070f]/85" />
      <StarField />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-canvas via-canvas/60 to-transparent" />
    </div>

    <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-20 pt-36 sm:px-8 lg:px-12">
      <Overline>Proiect principal · Asociația Art &amp; Outdoor România</Overline>
      <h1 data-testid="rise27-page-title" className="mt-6">
        <RiseWordmark className="text-6xl sm:text-7xl lg:text-8xl" testid="rise27-hero-wordmark" />
        <span className="mt-5 block font-display text-2xl font-black uppercase leading-[1] tracking-tight text-neon sm:text-4xl lg:text-5xl">
          Festivalul cetății
        </span>
      </h1>
      <FadeUp delay={0.5}>
        <p className="mt-8 max-w-xl text-base leading-relaxed text-neutral-300 sm:text-lg">
          Două zile de concerte cu muzica ascultată de tineri — de la hip-hop și
          trap la pop, dance, rock și electronic — între bastioanele Cetății Alba
          Carolina, iar istoria orașului se transformă în experiență live.
        </p>
      </FadeUp>
      <FadeUp delay={0.62}>
        <p className="mt-5 max-w-xl text-sm leading-relaxed text-neutral-400">
          Face parte din proiectul{" "}
          <Link
            to="/bushido-vs-bullying"
            data-testid="rise27-bushido-link"
            className="text-neon underline-offset-4 hover:underline"
          >
            Bushido vs. Bullying
          </Link>{" "}
          — promovăm respectul, incluziunea și zero toleranță la bullying.
        </p>
      </FadeUp>
      <FadeUp delay={0.65} className="mt-10 flex flex-wrap gap-3">
        <span
          data-testid="rise27-date-badge"
          className="flex items-center gap-2 rounded-full border border-neon/40 bg-canvas/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon backdrop-blur-sm"
        >
          <CalendarDays className="h-3.5 w-3.5" /> Vara 2027 · Cetatea Alba Carolina
        </span>
        <span className="flex items-center gap-2 rounded-full border border-white/15 bg-canvas/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 backdrop-blur-sm">
          <MapPin className="h-3.5 w-3.5" /> Alba Iulia, România
        </span>
        <span className="flex items-center gap-2 rounded-full border border-white/15 bg-canvas/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 backdrop-blur-sm">
          2 zile · 4 scene · 7 puncte de istorie
        </span>
      </FadeUp>
      <FadeUp delay={0.8}>
        <a
          href="#lista-asteptare"
          data-testid="rise27-hero-waitlist-cta"
          className="group mt-10 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white"
        >
          Vreau bilet / Anunță-mă
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </FadeUp>
    </div>
  </section>
);

const Program = () => (
  <section
    data-testid="rise27-program-section"
    className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32"
  >
    <FadeUp>
      <Overline>Program</Overline>
      <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
        Programul se anunță <span className="text-neon">în curând</span>
      </h2>
    </FadeUp>
    <FadeUp delay={0.1}>
      <div
        data-testid="rise27-program-coming-soon"
        className="mt-10 grid grid-cols-1 gap-8 rounded-3xl border border-white/10 bg-surface p-8 sm:p-12 lg:grid-cols-12"
      >
        <div className="lg:col-span-7">
          <p className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-flame">
            <AlertTriangle className="h-4 w-4" /> În lucru
          </p>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-300">
            Lucrăm la programul festivalului — două zile de muzică, ateliere și
            trasee prin cetate. Publicăm orele și artiștii imediat ce totul e
            confirmat.
          </p>
          <a
            href="#lista-asteptare"
            data-testid="rise27-program-waitlist-cta"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white"
          >
            Anunță-mă când apare programul
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
        <div className="space-y-px self-center bg-white/10 lg:col-span-5">
          {[
            { k: "2 zile", v: "Vineri & Sâmbătă" },
            { k: "4 scene", v: "Mare · Bastion · Kidz · Ateliere" },
            { k: "7 puncte", v: "Traseu de istorie prin cetate" },
          ].map((r) => (
            <div key={r.k} className="flex items-baseline gap-5 bg-surface py-5">
              <span className="font-display text-xl font-black text-neon">{r.k}</span>
              <span className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-neutral-400">
                {r.v}
              </span>
            </div>
          ))}
        </div>
      </div>
    </FadeUp>
  </section>
);

const Stages = () => (
  <section
    data-testid="rise27-stages-section"
    className="border-t border-white/10 bg-canvas"
  >
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32">
      <FadeUp>
        <Overline>Scene &amp; zone</Overline>
        <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
          Patru <span className="text-neon">lumi</span> în aceeași cetate
        </h2>
      </FadeUp>
      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2">
        {STAGES.map(({ icon: Icon, ...s }, i) => (
          <FadeUp key={s.id} delay={i * 0.08}>
            <div
              data-testid={`rise27-${s.id}`}
              className={`group h-full rounded-3xl border border-white/10 bg-surface p-8 transition-colors duration-500 hover:border-neon/50 ${
                i % 2 === 1 ? "md:mt-10" : ""
              }`}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-carddark text-neon transition-transform duration-500 group-hover:-rotate-6">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-6 font-display text-xl font-black uppercase tracking-tight text-white">
                {s.name}
              </h3>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-flame">
                {s.place}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-neutral-400">{s.text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Lineup = () => (
  <section
    data-testid="rise27-lineup-section"
    className="border-t border-white/10 bg-surface"
  >
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32">
      <FadeUp>
        <Overline>Line-up</Overline>
        <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
          Artiștii <span className="text-neon">se anunță</span>
        </h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-400">
          Negocierile cu artiștii sunt în desfășurare. Fiecare nume confirmat
          apare aici primul — iar pe listă afli înaintea tuturor.
        </p>
      </FadeUp>
      <div className="mt-14 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {ARTISTS.map((a, i) => (
          <FadeUp key={a.id} delay={i * 0.06} className="bg-surface">
            <div
              data-testid={`rise27-${a.id}`}
              className="group relative h-full overflow-hidden px-7 py-12"
            >
              <span className="absolute inset-0 -translate-y-full bg-neon transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0" />
              <div className="relative">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-flame transition-colors duration-500 group-hover:text-canvas">
                  {a.slot}
                </p>
                <h3 className="mt-4 font-display text-2xl font-black uppercase tracking-tight text-white transition-colors duration-500 group-hover:text-canvas">
                  {a.name}
                </h3>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 transition-colors duration-500 group-hover:text-canvas/70">
                  {a.tag}
                </p>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Waitlist = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, {
        name: form.name.trim() || null,
        email: form.email,
      });
      setDone(true);
      toast.success(
        data.already_registered
          ? "Ești deja pe listă — te anunțăm primul!"
          : "Te-ai înscris! Îți scriem imediat ce avem datele festivalului."
      );
      setForm({ name: "", email: "" });
    } catch (err) {
      toast.error("Ceva nu a mers bine. Te rugăm să încerci din nou.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="lista-asteptare"
      data-testid="rise27-waitlist-section"
      className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32"
    >
      <FadeUp className="overflow-hidden rounded-3xl border border-neon/25 bg-surface">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="p-8 sm:p-12 lg:col-span-7">
            <Overline>Lista de așteptare</Overline>
            <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl">
              Vreau bilet la <span className="text-neon">RISE 27'</span>
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-neutral-400">
              Lasă-ne emailul și te anunțăm primul când se deschid biletele,
              când apar artiștii și când confirmăm data exactă.
            </p>

            <form data-testid="rise27-waitlist-form" onSubmit={submit} className="mt-10">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="waitlist-name"
                    className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400"
                  >
                    Nume (opțional)
                  </label>
                  <input
                    id="waitlist-name"
                    data-testid="rise27-waitlist-input-name"
                    type="text"
                    maxLength={100}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Numele tău"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label
                    htmlFor="waitlist-email"
                    className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400"
                  >
                    Email
                  </label>
                  <input
                    id="waitlist-email"
                    data-testid="rise27-waitlist-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="nume@email.ro"
                    className={inputCls}
                  />
                </div>
              </div>
              <button
                type="submit"
                data-testid="rise27-waitlist-submit"
                disabled={loading}
                className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Se înscrie...
                  </>
                ) : (
                  <>
                    Înscrie-mă pe listă
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
              {done && (
                <p
                  data-testid="rise27-waitlist-success"
                  className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-neon"
                >
                  Ești pe listă · te anunțăm primul
                </p>
              )}
            </form>
            <p className="mt-6 text-xs leading-relaxed text-neutral-500">
              Folosim emailul doar pentru anunțuri despre RISE 27'. Te poți
              dezabona oricând. Vrei să fii voluntar sau partener?{" "}
              <Link
                to="/contact"
                data-testid="rise27-waitlist-contact-link"
                className="text-neon underline-offset-4 hover:underline"
              >
                Scrie-ne
              </Link>
              .
            </p>
          </div>
          <div className="relative min-h-[240px] lg:col-span-5">
            <img
              src="/assets/festival-alba-zi.webp"
              alt="Public la festival în Alba Iulia"
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent lg:bg-gradient-to-r" />
          </div>
        </div>
      </FadeUp>
    </section>
  );
};

export default function Rise27() {
  return (
    <>
      <Hero />
      <Program />
      <Stages />
      <TurCetate />
      <Lineup />
      <Waitlist />
    </>
  );
}
